// v1.32 P0: Tabs 原子组件
// 移动端优先 — 桌面端支持 underline / pill 两种风格, 移动端总是 pill (横滑可滚)
//
// 设计:
// - a11y: 用 [role="tablist"] / [role="tab"] / [role="tabpanel"], 键盘 ←→ 切换
// - 受控: value + onChange 必填 (避免 un-controlled 状态)
//
// 用法:
//   const [tab, setTab] = useState('info');
//   <Tabs value={tab} onChange={setTab} items={[
//     { value: 'info', label: '信息', icon: 'user' },
//     { value: 'security', label: '安全', icon: 'lock' },
//   ]} />
//   {tab === 'info' && <InfoPanel />}
import { type ReactNode, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon';

export interface TabItem {
  value: string;
  /** tab 标签 (中文) */
  label: string;
  /** 左侧 icon (Icon name) */
  icon?: string;
  /** 移动端隐藏文字 (只显示 icon) */
  iconOnly?: boolean;
  /** tab 角标 (数字) */
  badge?: number | string;
  /** 禁用 */
  disabled?: boolean;
}

export interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  items: TabItem[];
  /** 'underline' (默认, 桌面用) | 'pill' (移动端友好) */
  variant?: 'underline' | 'pill';
  /** 容器 className */
  className?: string;
  /** a11y label for tablist */
  ariaLabel?: string;
}

export function Tabs({
  value,
  onChange,
  items,
  variant = 'underline',
  className,
  ariaLabel,
}: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // 键盘 ←→ 切换
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const idx = items.findIndex((it) => it.value === value);
      if (idx < 0) return;
      let next = idx;
      if (e.key === 'ArrowLeft') next = idx - 1;
      if (e.key === 'ArrowRight') next = idx + 1;
      // 跳过 disabled
      while (next >= 0 && next < items.length && items[next]?.disabled) {
        next += e.key === 'ArrowLeft' ? -1 : 1;
      }
      if (next >= 0 && next < items.length) {
        const target = items[next];
        if (target) onChange(target.value);
        e.preventDefault();
      }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [items, value, onChange]);

  const isPill = variant === 'pill';

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={twMerge(
        isPill
          ? 'inline-flex items-center gap-1 p-1 bg-surface-2 rounded-lg overflow-x-auto max-w-full'
          : 'flex items-center gap-4 border-b border-line overflow-x-auto',
        className,
      )}
    >
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`tabpanel-${it.value}`}
            id={`tab-${it.value}`}
            tabIndex={active ? 0 : -1}
            disabled={it.disabled}
            onClick={() => onChange(it.value)}
            className={twMerge(
              'inline-flex items-center gap-1.5 shrink-0',
              'transition-colors duration-150',
              'focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
              'disabled:opacity-40 disabled:cursor-not-allowed',
              isPill
                ? [
                    'px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md',
                    active
                      ? 'bg-surface text-ink font-medium shadow-sm'
                      : 'text-ink-2 hover:text-ink',
                  ]
                : [
                    'pb-2 text-xs sm:text-sm border-b-2 -mb-px',
                    active
                      ? 'border-accent text-ink font-medium'
                      : 'border-transparent text-ink-2 hover:text-ink hover:border-ink-3',
                  ],
            )}
          >
            {it.icon && <Icon name={it.icon} size="sm" aria-hidden />}
            <span className={twMerge(it.iconOnly && 'sr-only sm:not-sr-only')}>{it.label}</span>
            {it.badge !== undefined && (
              <span
                className={twMerge(
                  'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-medium rounded-full',
                  active ? 'bg-accent text-white' : 'bg-surface-2 text-ink-3 border border-line',
                )}
              >
                {it.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** TabPanel — 配合 Tabs 使用, 简化 a11y 属性 */
export function TabPanel({
  value,
  active,
  children,
  className,
}: {
  value: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      className={className}
    >
      {children}
    </div>
  );
}
